import { Clipboard } from '@angular/cdk/clipboard';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  EventEmitter,
  inject,
  input,
  Input,
  model,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import {
  BuySpecs,
  Invoices,
  Samples,
  SampleShipmentRequests,
  ShipmentFulfillments,
} from '@etoh/database/angular';

import {
  Agreements,
  Contracts,
  DataTypeKeys,
  Items,
  SampleShipments,
} from '@etoh/database/core';
import { AgGridAngular } from 'ag-grid-angular';
import {
  CellClickedEvent,
  ColDef,
  GetContextMenuItemsParams,
  GridOptions,
  GridReadyEvent,
  MenuItemDef,
  ProcessCellForExportParams,
} from 'ag-grid-community';
import copy from 'fast-copy';
import { cloneDeep, groupBy, isEmpty, keyBy, omit, pick, sortBy } from 'lodash';

import { CommonModule } from '@angular/common';
import { FlexLayoutModule } from '@angular/flex-layout';
import { RxPush } from '@rx-angular/template/push';
import { UserRoleApp } from 'apps/desktop/src/app/roles';
import { UserService } from 'apps/desktop/src/app/user.service';
import { groupByMultiple } from 'apps/desktop/src/app/utilities/groupByMultiple';
import { format, parseISO } from 'date-fns';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzEmptyModule } from 'ng-zorro-antd/empty';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import {
  firstValueFrom,
  lastValueFrom,
  Observable,
  shareReplay,
  Subject,
  switchMap,
  take,
  takeUntil,
} from 'rxjs';
import { AgreementsService } from '../../../agreements/agreements.service';
import { LogsService } from '../../../logs/logs.service';
import { StoreService } from '../../../store.service';
import {
  ListAction,
  ListActionFinal,
} from '../../interfaces/listOptions.interface';
import { isShowPageFormButton } from '../../interfaces/pageForm.interface';
import { Context, LegendAgGrid } from '../../list.interfaces';
import { ListService } from '../../list.service';
import { textComparator } from '../../params/comparators';
import { dateFilterParams } from '../../params/dateFilterParams';
import { numberFilterParams } from '../../params/numberFilterParams';
import { IdentifierComponent } from '../table/cells/identifier/identifier.component';
import { TooltipComponent } from '../table/components/tooltip/tooltip.component';
import { dateFormatter, getPlainTextFromHtml } from '../table/formatter';
import { StatusBarGridComponent } from './status-bar-grid/status-bar-grid.component';
import { IStatusPanelAngularComp } from './status-bar-grid/status-bar-grid.interface';

@Component({
  selector: 'etoh-list',
  templateUrl: './list.component.html',
  styleUrls: ['./list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    FlexLayoutModule,
    CommonModule,
    NzButtonModule,
    NzToolTipModule,
    RxPush,
    NzSpinModule,
    NzEmptyModule,
    AgGridAngular,
    NzIconModule,
  ],
})
export class ListComponent implements OnInit, OnDestroy {
  @ViewChild(AgGridAngular, { static: false }) agGrid!: AgGridAngular;

  @Input() context: Context | undefined;

  @Input() dataType: DataTypeKeys | undefined;
  @Input() data$: Observable<any[]>;
  extendedData$: Observable<any[]>;

  @Input() columnDefsDisplayed: ColDef<any>[] | undefined;

  @Input() firstColumnDefs: ColDef<any>[] = [];

  selectedDatas$ = model<any[]>([]);
  // $selectedDatas = toSignal(this.selectedDatas$.asObservable());

  actionsOnSelections = input<ListAction<any>[] | null | undefined>();
  actionsSelectionsDisplayed = computed<ListAction<any>[] | null | undefined>(
    () => {
      const actionsOnSelections = this.actionsOnSelections();
      if (Array.isArray(actionsOnSelections)) {
        return <ListAction<any>[]>[
          ...actionsOnSelections,
          {
            name: 'Download CSV',
            roles: <UserRoleApp[]>['user'],
            action: () => {
              this.logsService.add('download', {
                type: 'csv',
                dataType: this.dataType ?? 'unknown',
                cellsLength: this.selectedDatas$()?.length,
              });

              this.exportInCsv(true);
            },
          },
          {
            name: 'Download Excel',
            roles: <UserRoleApp[]>['user'],
            action: () => {
              this.logsService.add('download', {
                type: 'excel',
                dataType: this.dataType ?? 'unknown',
                cellsLength: this.selectedDatas$()?.length,
              });

              this.exportInExcel(true);
            },
          },
        ].filter((action) => {
          const user = this.userService.userBehaviourSubject$.value;
          return user ? isShowPageFormButton(action, user) : true;
        });
      }

      return this.actionsOnSelections();
    }
  );

  actionsSelectionsDisplayed2 = computed<ListActionFinal<any>[]>(() => {
    const buttons = this.actionsSelectionsDisplayed() ?? [];

    console.log('youpi', this.selectedDatas$());
    return buttons.map((button) => {
      return {
        ...button,
        action: () => {
          button.action(this.selectedDatas$());
          // TODO hack
          // Action should be promise to handle finish
          this.agGrid.api.deselectAll();
          this.setStatusPanel([]);
          this.selectedDatas$.set([]);
        },
        disabled: button.isDisabled?.(this.selectedDatas$()) ?? false,
        tooltip: button.getTooltip?.(this.selectedDatas$()) ?? undefined,
      };
    });
  });

  @Input() sideBar = true;
  @Input() height = 'calc(100vh - 160px)';
  displayedHeight = this.height;

  @Input() canFilter = true;
  @Input() excludedFromFilters: string[] = [];

  gridOptions: GridOptions;

  tooltipShowDelay = 3000;
  tooltipHideDelay = 2000;

  hasFilters = false;
  @Output() filterChanged = new EventEmitter<any>();

  @Output() gridReady = new EventEmitter<void>();

  lastFilterUpdateFromExternal = false;

  defaultColDef: ColDef;

  destroy$ = new Subject();

  legends: LegendAgGrid[] = [];

  private _saveColumnStateNumber = 0;

  private cd = inject(ChangeDetectorRef);
  private listService = inject(ListService);
  private storeService = inject(StoreService);
  private logsService = inject(LogsService);
  private clipboard = inject(Clipboard);
  private userService = inject(UserService);
  private agreementService = inject(AgreementsService);

  async ngOnInit() {
    this.gridOptions = {
      alwaysShowHorizontalScroll: true,
      enableRangeSelection: true,
      enableBrowserTooltips: false,
      multiSortKey: 'ctrl',
      sideBar: {
        toolPanels: [
          {
            id: 'columns',
            labelDefault: 'Columns',
            labelKey: 'columns',
            iconKey: 'columns',
            toolPanel: 'agColumnsToolPanel',
            minWidth: 225,
            maxWidth: 225,
            width: 225,
          },
          {
            id: 'filters',
            labelDefault: 'Filters',
            labelKey: 'filters',
            iconKey: 'filter',
            toolPanel: 'agFiltersToolPanel',
            minWidth: 180,
            maxWidth: 400,
            width: 250,
          },
        ],
        position: 'right',
      },
      getContextMenuItems: this.getContextMenuItems.bind(this),
      statusBar: {
        statusPanels: [
          { statusPanel: 'agTotalAndFilteredRowCountComponent', align: 'left' },
          { statusPanel: 'agTotalRowCountComponent', align: 'center' },
          { statusPanel: 'agFilteredRowCountComponent' },
          { statusPanel: 'agSelectedRowCountComponent' },
          { statusPanel: 'agAggregationComponent' },
          { statusPanel: StatusBarGridComponent, key: 'StatusBarGrid' },
        ],
      },
      rowClassRules: this.dataType
        ? this.listService.getRowClassRules(this.dataType)
        : undefined,
    };

    if (this.dataType) {
      this.legends = this.listService.getRowLegends(this.dataType);
    }

    this.extendedData$ = this.data$.pipe(
      switchMap(async (datas) => {
        datas = copy(datas);

        if (this.dataType === 'shipmentFulfillments') {
          const jointDatas = await this.storeService.getJointsDatas([
            'agreements',
            'invoices',
            'items',
          ]);

          const invoicesBy = groupBy(
            jointDatas.invoices,
            'shipmentFulfillmentId'
          );

          datas = datas
            .map((data) =>
              this.storeService.jointObject<ShipmentFulfillments>(
                data,
                'agreementId',
                'agreement',
                jointDatas.agreements
              )
            )
            .map((data) =>
              this.storeService.jointObject<ShipmentFulfillments>(
                data,
                'itemId',
                'item',
                jointDatas.items
              )
            );

          datas = datas.map((data) => {
            const invoices = invoicesBy[data.id];
            if (invoices?.length) {
              data.invoices = invoices;

              data.invoice = invoices[0];
            }
            return data;
          });

          return datas;
        } else if (this.dataType === 'contracts') {
          const jointDatas = await this.storeService.getJointsDatas([
            'agreements',
            'companies',
          ]);

          datas = datas.map((data) =>
            this.storeService.jointObject<Contracts>(
              data,
              'agreementId',
              'agreement',
              jointDatas.agreements
            )
          );

          datas = datas.map((data) => {
            return {
              ...data,
              companiesInvolved: this.agreementService.getCompaniesInvolved(
                data.agreement
              ),
            };
          });

          datas = datas.map((data) => {
            return {
              ...data,
              status: this.agreementService.getGlobalContractStatus(
                this.agreementService.getContractStatus(
                  data.companiesInvolved,
                  data.validationsContracts
                )
              ),
            };
          });
        } else if (this.dataType === 'sampleShipmentRequests') {
          const jointDatas = await this.storeService.getJointsDatas([
            'items',
            'buySpecs',
          ]);

          datas = datas.map((data) =>
            this.storeService.jointObject<SampleShipmentRequests>(
              data,
              'itemId',
              'item',
              jointDatas.items
            )
          );

          datas = datas.map((data) =>
            this.storeService.jointObject<SampleShipmentRequests>(
              data,
              'buySpecId',
              'buySpec',
              jointDatas.buySpecs
            )
          );

          const samplesByItemId: Record<number, Samples[]> = groupBy(
            await lastValueFrom(
              this.storeService.store.samples.data$.pipe(take(1))
            ),
            'itemId'
          );

          return datas.map((data) => {
            return {
              ...data,
              samplesByItemId: samplesByItemId[data.itemId] || [],
            };
          });
        } else if (this.dataType === 'samples') {
          const jointDatas = await this.storeService.getJointsDatas(['items']);

          datas = datas.map((data) =>
            this.storeService.jointObject<Samples>(
              data,
              'itemId',
              'item',
              jointDatas.items
            )
          );

          return datas;
        } else if (this.dataType === 'companies') {
          const agreements = await firstValueFrom(
            this.storeService.store.agreements.data$.pipe(take(1))
          );

          const extendedAgreements = agreements.map((agreement: Agreements) => {
            return {
              ...agreement,
              companiesId: [
                agreement.buyerCompanyId,
                agreement.sellerCompanyId,
                agreement.thirdPartyCompanyId,
              ].filter((r) => !!r),
            };
          });

          const groupByMultipleCompanies = groupByMultiple(
            extendedAgreements,
            'companiesId'
          );

          datas = datas.map((data) => {
            return {
              ...data,
              agreements: groupByMultipleCompanies[data.id] || [],
              agreementsCount: groupByMultipleCompanies[data.id]?.length || 0,
            };
          });

          return datas;
        } else if (this.dataType === 'invoices') {
          const jointDatas = await this.storeService.getJointsDatas([
            'agreements',
            'shipmentFulfillments',
          ]);

          const currenciesConversions = await firstValueFrom(
            this.storeService.store.currenciesConversions.data$.pipe(take(1))
          );

          datas = datas
            .map((data) =>
              this.storeService.jointObject<Invoices>(
                data,
                'agreementId',
                'agreement',
                jointDatas.agreements
              )
            )
            .map((data) => {
              return this.storeService.jointObject<Invoices>(
                data,
                'shipmentFulfillmentId',
                'shipmentFulfillment',
                jointDatas.shipmentFulfillments
              );
            });

          return datas;
        } else if (this.dataType === 'buySpecs') {
          const agreements: Agreements[] = await firstValueFrom(
            this.storeService.store.agreements.data$.pipe(take(1))
          );

          const agreementsGroupByBuySpecsId = groupByMultiple(
            agreements,
            'buyerSpecsId'
          );

          datas = datas.map((data: BuySpecs) => ({
            ...data,
            agreementsIds: agreementsGroupByBuySpecsId[data.id]?.map(
              (agreement) => agreement.id
            ),
            // agreements: agreementsGroupByBuySpecsId[data.id],
          }));

          return datas;
        } else if (this.dataType === 'items') {
          const samples: Samples[] = await firstValueFrom(
            this.storeService.store.samples.data$.pipe(take(1))
          );

          const samplesGroupByItemId = groupBy(samples, 'itemId');

          datas = datas.map((data: Items) => ({
            ...data,
            samples: samplesGroupByItemId[data.id],
          }));

          return datas;
        } else if (this.dataType === 'sampleShipments') {
          const sampleShipmentRequest: SampleShipmentRequests[] =
            await firstValueFrom(
              this.storeService.store.sampleShipmentRequests.data$.pipe(take(1))
            );

          const sampleShipmentRequestGroupBySampleShipmentId = groupBy(
            sampleShipmentRequest,
            'sampleShipmentId'
          );

          datas = datas.map((data: SampleShipments) => ({
            ...data,
            sampleShipmentRequests:
              sampleShipmentRequestGroupBySampleShipmentId[data.id] || [],
          }));

          return datas;
        } else if (this.dataType === 'agreements') {
          const shipmentFulfillments: ShipmentFulfillments[] =
            await firstValueFrom(
              this.storeService.store.shipmentFulfillments.data$.pipe(take(1))
            );

          const shipmentFulfillmentsByAgreementId = groupBy(
            shipmentFulfillments,
            'agreementId'
          ) as Record<number, ShipmentFulfillments[]>;

          const invoices: Invoices[] = await firstValueFrom(
            this.storeService.store.invoices.data$.pipe(take(1))
          );

          datas = datas.map((data: Agreements) => ({
            ...data,
            invoices: invoices.filter(
              (invoice) => invoice.agreementId === data.id
            ),
            shipmentFulfillments:
              shipmentFulfillmentsByAgreementId[data.id] || [],
          }));
        }

        return datas;
      }),
      shareReplay(1)
    );

    this.defaultColDef = {
      floatingFilter: true,
      sortable: true,
      filter: this.canFilter,
      resizable: true,
      tooltipComponent: TooltipComponent,
      cellDataType: false,
    };

    this.context = {
      dataType: this.dataType,
      identifierLink: true,
      ...(this.context ?? {}),
    };

    let columnDefs: any[];

    if (this.columnDefsDisplayed?.length) {
      columnDefs = this.listService.postProcessColumnDefs(
        this.columnDefsDisplayed
      );
    } else {
      if (!this.dataType) {
        throw new Error('dataType is required !');
      }

      columnDefs = await this.listService.getColumnDefinitions(
        this.dataType as any
      );

      columnDefs = cloneDeep(columnDefs);

      const identifier: ColDef = {
        headerName: 'Id',
        width: 133,
        cellRenderer: IdentifierComponent,
        filter: 'agNumberColumnFilter',
        pinned: true,
        field: 'id',
        colId: 'id',
        lockPosition: 'left',
        suppressColumnsToolPanel: true,
      };

      // push action var to show 'action' column before 'id' column
      if (this.actionsSelectionsDisplayed()?.length) {
        columnDefs.splice(
          0,
          0,
          this.listService.transformFirstColumnToActionColumn(identifier)
        );
      } else {
        columnDefs.splice(0, 0, identifier);
      }

      columnDefs = this.listService.postProcessColumnDefs([
        ...columnDefs,
        {
          field: 'createdBy',
          dataType: 'users',
          roles: ['user'],
        },
        {
          field: 'createdAt',
          valueFormatter: dateFormatter,
          filter: 'agDateColumnFilter',
        },
        {
          field: 'updatedAt',
          valueFormatter: dateFormatter,
          filter: 'agDateColumnFilter',
        },
        {
          field: 'archivedAt',
          valueFormatter: dateFormatter,
          filter: 'agDateColumnFilter',
          roles: ['user'],
        },
      ]);
    }

    if (columnDefs?.length) {
      // Should be refactored to avoid usage of first column defs
      columnDefs = [...this.firstColumnDefs, ...columnDefs];

      this.columnDefsDisplayed = this.orderColumns(
        columnDefs.map((a: any) => {
          const defaultFilters = !a.filter
            ? {
                filter: 'agMultiColumnFilter',
                comparator: textComparator,
                filterParams: {
                  filters: [
                    {
                      filter: 'agTextColumnFilter',
                      display: 'subMenu',
                      filterParams: {
                        buttons: ['reset'],
                      },
                    },
                    {
                      filter: 'agSetColumnFilter',
                      filterParams: {
                        buttons: ['reset'],
                      },
                    },
                  ],
                },
              }
            : {};

          const x = {
            ...a,
            // tooltip field
            tooltipField: a.field,
            // check if col need comparator for date filter
            filterParams: this.getFilterParams(a.filter),
            ...defaultFilters,
            // set field to retreive value in TooltipComponent
            tooltipComponentParams: { field: a.field },
            cellClass:
              a.filter === 'agNumberColumnFilter'
                ? 'ag-right-aligned-cell'
                : undefined,
          };
          return x;
        })
      );

      console.log('columnDefsDisplayed', this.columnDefsDisplayed);
      this.cd.detectChanges();
    }
  }

  private getFilterParams(filter: string) {
    switch (filter) {
      case 'agDateColumnFilter':
        return dateFilterParams;
      case 'agNumberColumnFilter':
        return numberFilterParams;
      default:
        return {
          buttons: ['reset'],
        };
    }
  }

  exportInExcel(selected = false) {
    this.agGrid?.api?.exportDataAsExcel({
      onlySelected: selected,
      skipRowGroups: true,

      processCellCallback: this.processCellCallback,

      fileName: `${this.dataType}-${new Date().toLocaleDateString()}.xlsx`,
      columnKeys: this.agGrid.api
        .getAllDisplayedColumns()
        .filter((a) => a.getColId() !== 'action'),
    });
  }

  exportInCsv(selected = false) {
    this.agGrid?.api?.exportDataAsCsv({
      onlySelected: selected,
      processCellCallback: this.processCellCallback,

      skipRowGroups: true,

      fileName: `${this.dataType}-${new Date().toLocaleDateString()}.csv`,
      columnKeys: this.agGrid.api
        .getAllDisplayedColumns()
        .filter((a) => a.getColId() !== 'action'),
    });
  }

  processCellCallback = (params: ProcessCellForExportParams) => {
    const filterName = (params?.column as any)?.userProvidedColDef?.filter;

    const exportType = (params?.column as any)?.userProvidedColDef?.exportType;

    if (filterName === 'agDateColumnFilter') {
      console.log('params agDateColumnFilter', params);
      if (!params.value) {
        return '';
      }

      return format(parseISO(params.value), 'yyyy-MM-dd');
    }

    if (exportType === 'htmlToText') {
      if (!params.value) {
        return '';
      }
      return getPlainTextFromHtml(params.value);
    }

    return params.value;
  };

  ngOnDestroy(): void {
    this.destroy$.next(null);
    this.destroy$.complete();
  }

  onGridReady(params: GridReadyEvent) {
    console.log('context', this.agGrid.context);
    this.gridReady.emit();

    this.data$.pipe(takeUntil(this.destroy$)).subscribe((data) => {
      if (this.selectedDatas$()?.length) {
        const selectedRowsBy = keyBy(this.selectedDatas$(), 'id');
        setTimeout(() => {
          this.agGrid?.api.forEachNode((node: any) => {
            if (selectedRowsBy[node.data?.id]) {
              node.setSelected(true);
            }
          });
        });
      }
    });

    this.applyColumnState();
    this.applyFilterState();
  }

  onCellClicked(e: CellClickedEvent): void {
    console.log('cellClicked', e);
  }

  clearSelection(): void {
    this.agGrid?.api?.deselectAll();
  }

  public onSelectionChanged(event: any) {
    const selectedRows = this.agGrid.api.getSelectedRows();

    this.setStatusPanel(selectedRows);
    this.selectedDatas$.set(selectedRows);
  }

  public getRowId(params: any) {
    return (params.data.id as number).toString();
  }

  public setStatusPanel(selectedRows: any[]) {
    const statusPanel =
      this.agGrid?.api?.getStatusPanel<IStatusPanelAngularComp>(
        'StatusBarGrid'
      );

    statusPanel?.update?.(selectedRows);
  }

  public resetFilters() {
    const savedState = this.agGrid.api.getFilterModel();

    const newFilterModel = pick(savedState, this.excludedFromFilters);
    this.agGrid.api.setFilterModel(newFilterModel);
  }

  public getListKey(type: string): string {
    return `${type}-${this.dataType}-${this.cleanLocationPathName(
      window.location.pathname
    )}`;
  }

  // We find a better page name because id can change in url
  // also, we need to unify add and edit mode to form mode
  public cleanLocationPathName(pathName: string): string {
    if (pathName.includes('/add')) {
      return pathName.replace(/\/add$/gm, 'form');
    } else if (pathName.includes('/edit')) {
      return pathName.replace(/\/edit\/\d+$/gm, '/form');
    }

    return pathName;
  }

  public saveColumnState(event: any, type: string) {
    this._saveColumnStateNumber++;
    if (this._saveColumnStateNumber === 1) {
      return;
    }
    const savedState = this.agGrid.api.getColumnState();
    this.userService.updateJsonbField(
      'savedColumns',
      this.getListKey('column'),
      savedState
    );
  }

  public applyColumnState() {
    const savedState: any = this.userService.getJsonField(
      'savedColumns',
      this.getListKey('column')
    );

    if (savedState) {
      this.agGrid.api.applyColumnState({
        state: savedState,
        applyOrder: true,
      });
    }
  }

  public saveFilterState() {
    const savedState = this.agGrid.api.getFilterModel();

    this.userService.updateJsonbField(
      'savedColumns',
      this.getListKey('filter'),
      savedState
    );

    if (this.lastFilterUpdateFromExternal) {
      this.lastFilterUpdateFromExternal = false;
    } else {
      this.filterChanged.next(savedState);
    }
    this.setHasfilters(savedState);
  }

  public applyFilterState() {
    console.log('applyFilterState');

    const savedState = this.userService.getJsonField(
      'savedColumns',
      this.getListKey('filter')
    );

    if (savedState) {
      this.agGrid.api.setFilterModel(savedState);
      this.filterChanged.next(savedState);
      this.setHasfilters(savedState);
    } else {
      // We should say that there is no initial value so the first sort will be applied
      this._saveColumnStateNumber = 1;
    }
  }

  private setHasfilters(state: any) {
    if (isEmpty(omit(state, this.excludedFromFilters))) {
      this.hasFilters = false;
    } else {
      this.hasFilters = true;
    }
  }

  public orderColumns(columns: any[]): any[] {
    const savedState: any[] = this.userService.getJsonField(
      'savedColumns',
      this.getListKey('column')
    );

    if (savedState) {
      const x = sortBy(columns, (col) => {
        const index = savedState.findIndex(
          (a: any) => a.colId === col.field || a.colId === col.colId
        );

        if (index === -1) {
          return 999;
        }

        return index;
      });
      return x;
    } else {
      return columns;
    }
  }

  public getContextMenuItems(
    params: GetContextMenuItemsParams
  ): (string | MenuItemDef)[] {
    let result: (string | MenuItemDef)[] = [
      'separator',
      'copy',
      'copyWithHeaders',
      'copyWithGroupHeaders',
      'separator',
      'chartRange',
    ];

    const id = params?.node?.data.id;
    const dataType = params?.context?.dataType;

    if (id && dataType) {
      result = [
        {
          name: 'Open in new tab',
          action: () => {
            window.open('/features/' + dataType + '/edit/' + id, '_blank');
            console.log('params', params);
          },
          cssClasses: ['bold'],
        },
        {
          name: 'CSV export',
          action: () => {
            this.exportInCsv(false);
          },
          icon: '<span class="ag-icon ag-icon-csv" unselectable="on" role="presentation"></span>',
        },
        {
          name: 'Excel export',
          action: () => {
            this.exportInExcel(false);
          },
          icon: '<span class="ag-icon ag-icon-excel" unselectable="on" role="presentation"></span>',
        },
        ...result,
      ];
    }

    return result;
  }

  // Should be a arrow function
  public sendToClipboard: any = (params: any) => {
    this.clipboard.copy(params.data);
    this.logsService.add('copy', {
      type: 'data',
      dataType: this.dataType ?? 'unknown',
      cellsLength: params.data?.split('\n')?.length,
    });
  };

  // dynamic height is 32px
  public lessHeight() {
    if (this.height === 'calc(100vh - 160px)') {
      this.displayedHeight = 'calc(100vh - 192px)';
    }
    return true;
  }

  public resetHeight() {
    this.displayedHeight = this.height;
    return false;
  }
}
